import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';

import { Auth0Service } from '@geneplanet/ngx-auth0';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './analytics.service';
import {
  AnalyticsService as Analytics,
  AnalyticsIdentifyType,
} from '@geneplanet/ngx-utils';
import {
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  switchMap,
} from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Auth0Account } from './accounts/interfaces/accounts.types';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';
import { LayoutService } from './core/services/layout.service';
import { Layout } from './core/interfaces';
import { LanguageService } from './shared/services/language.service';
import { SideMenuService } from './core/services/side-menu.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { CommonService } from '@geneplanet/ngx-shop/src/lib/shared';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  GpuMediaQueryBreakpoints,
  GPU_MEDIA_QUERIES,
} from '@geneplanet/ngx-ui/src/lib/layout';
import { SentryErrorService } from './sentry-error.service';
import { IntentService } from './shared/services/intent/intent.service';
import { UserIntentState } from './shared/interfaces/intent.interface';
import { of } from 'rxjs';
import { MobileFooterComponent } from './core/components/mobile-footer/mobile-footer.component';
import { BottomOffsetComponent } from './shared/components/bottom-offset/bottom-offset.component';
import { HoverToggleDirective } from './core/directives/hover-toggle.directive';
import { SideMenuComponent } from './core/components/side-menu/side-menu.component';

import { HeaderComponent } from './core/components/header/header.component';
import { SystemNotificationsService } from './core/services/system-notifications.service';
import { SnowComponent } from './shared/components/snow/snow.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    HeaderComponent,
    SideMenuComponent,
    HoverToggleDirective,
    RouterOutlet,
    BottomOffsetComponent,
    MobileFooterComponent,
    SnowComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly auth0 = inject(Auth0Service);
  private readonly analytics = inject(AnalyticsService);
  layoutService = inject(LayoutService);
  private commonService = inject(CommonService);
  private _localize = inject(LocalizeRouterService);
  languageService = inject(LanguageService);
  private sideMenuService = inject(SideMenuService);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);
  private modals = inject(NgbModal);
  private analyticsService = inject(Analytics);
  private sentryErrorService = inject(SentryErrorService);
  private intentService = inject(IntentService);
  private systemNotifications = inject(SystemNotificationsService);

  public get localize(): LocalizeRouterService {
    return this._localize;
  }
  public set localize(value: LocalizeRouterService) {
    this._localize = value;
  }
  readonly layout = signal<Layout>(undefined);
  environment = environment;
  readonly account = signal<Auth0Account>(undefined);
  language: string;
  hubspotNewsletter: {
    formId: string;
    portalId: string;
    submitTextKey: string;
  };
  readonly mainMarginTop = signal(0);
  readonly sideMenuWidth = signal<number>(undefined);
  readonly isCheckout = signal<boolean>(false);
  readonly isXlUp = signal<boolean>(false);

  christmasSpirit: {
    isChristmas: boolean;
    showSnowflakes: boolean;
  } = {
    isChristmas: false,
    showSnowflakes: true,
  };

  private readonly routerUrl = signal<string>(undefined);
  private readonly isReloading = signal<boolean>(false);

  readonly hasSideMenu = computed(() => {
    return (
      (this.account() && !this.layout()?.sideMenu?.hide) ||
      this.layout()?.sideMenu?.hasClass
    );
  });

  readonly hasFooterMenu = computed(() => {
    return this.account() && !this.layout()?.footer?.hideMobileNav;
  });

  constructor() {
    this.christmasSpirit.isChristmas = this.isChristmasTime();
    this.sideMenuService.state$.subscribe(({ collapsed, opened }) => {
      if (!opened) {
        this.sideMenuWidth.set(0);
      } else {
        this.sideMenuWidth.set(
          collapsed ? 72 : this.sideMenuService.sideMenuWidth
        );
      }
    });

    const location = this.commonService.getLocationFromCode(
      this.localize.parser.currentLang
    );

    this.languageService.onLocationChange(location);

    this.intentService.state$
      .pipe(
        first(),
        switchMap((state: UserIntentState) =>
          state ? this.intentService.verifyIntentActive(state) : of(null)
        )
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.analytics.init();

    if (environment.rudderStackEnabled) {
      this.sentryErrorService.errorOnMissingUserId();
    }

    this.auth0.tokens.current$
      .pipe(
        map((token: string) =>
          new JwtHelperService().decodeToken(JSON.parse(token || '{}').idToken)
        ),
        delay(0),
        filter(() => !this.isReloading())
      )
      .subscribe({
        next: (account: Auth0Account) => {
          this.account.set(account);

          this.sentryErrorService.setAccount(account);

          // Add user to Sentry
          if (environment.sentry?.logUser) {
            Sentry.setUser({ auth0: account?.sub });
          }
          // If user isn't identified while logged in
          if (environment.rudderStackEnabled && account) {
            this.identifyUser(account);
          }
        },
        error: (error) => {
          Sentry.captureMessage(
            `Failed to verify if user is identified: ${error}`
          );
        },
      });

    this.auth0.tokens.otherPageChange$.subscribe(() => {
      this.isReloading.set(true);
      window.location.reload();
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: NavigationEnd) => {
          // Remove additional parameter from fragment - HS
          const splits = event.url.split('#');
          if (splits.length > 1) {
            splits[1] = splits[1].split('-')[0];
          }
          return splits.join('#');
        }),
        distinctUntilChanged()
      )
      .subscribe((url: string) => {
        this.routerUrl.set(url);
        this.modals.dismissAll();
        this.systemNotifications.currentUrl.set(url);
      });

    this.layoutService.layout$.subscribe((layout: Layout) => {
      this.layout.set(layout);
      this.isCheckout.set(layout?.footer?.bottomOffset === 'checkout');
    });
    this.layoutService.headerHeightChange$.subscribe((height: number) =>
      this.mainMarginTop.set(height)
    );

    this.breakpointObserver
      .observe(GPU_MEDIA_QUERIES[GpuMediaQueryBreakpoints.XL_UP])
      .subscribe((value: BreakpointState) => this.isXlUp.set(value.matches));
  }

  public showBottomOffset(): boolean {
    return !!this.layout()?.footer?.bottomOffset;
  }

  public isProductsPage(): boolean {
    return this.routerUrl()?.startsWith('/products/');
  }

  public isCartOrCheckoutPage(): boolean {
    return (
      this.routerUrl()?.startsWith('/cart') ||
      this.routerUrl()?.startsWith('/checkout/')
    );
  }

  public identifyUser(account: Auth0Account) {
    this.analyticsService
      .loaded()
      .pipe(
        filter((loaded) => loaded),
        first()
      )
      .subscribe(() => {
        this.analyticsService.setUser({
          userId: account.sub,
          traits: {
            type: AnalyticsIdentifyType.HI_NORMAL,
            customerId: account['http://schemas.nomnio.com/2017/5/customer'],
          },
        });
      });
  }

  isChristmasTime(): boolean {
    const date: Date = new Date();
    const month = date.getMonth();
    const day = date.getDate();

    //show only between 15th of December and 7th of January
    return (
      (month === 11 && day >= 15) || // >= Dec 15
      (month === 0 && day <= 7) // <= Jan 7
    );
  }
}
